// src/context/modules/servicesModule.js
import { useState, useCallback } from "react";
import {
  fetchServices,
  updateService,
  createService,
  deleteService,
} from "../../api/servicesApi";

export const useServicesModule = () => {
  // State
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  // Operations
  const refreshServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const newServices = await fetchServices();
      setServices(newServices);
      return newServices;
    } catch (error) {
      console.error("Error refreshing services:", error);
      throw error;
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const addService = useCallback(
    async (serviceData) => {
      setServicesLoading(true);
      try {
        const newService = await createService(serviceData);
        setServices((prev) => [...prev, newService]);
        return newService;
      } catch (error) {
        console.error("Error adding service:", error);
        throw error;
      } finally {
        await refreshServices();
        setServicesLoading(false);
      }
    },
    [refreshServices]
  );

  const updateServiceData = useCallback(
    async (id, serviceData) => {
      setServicesLoading(true);
      try {
        const updatedService = await updateService(id, serviceData);
        setServices((prev) =>
          prev.map((service) => (service.id === id ? updatedService : service))
        );
        return updatedService;
      } catch (error) {
        console.error("Error updating service:", error);
        throw error;
      } finally {
        await refreshServices();
        setServicesLoading(false);
      }
    },
    [refreshServices]
  );

  const removeService = useCallback(
    async (id) => {
      setServicesLoading(true);
      try {
        await deleteService(id);
        setServices((prev) => prev.filter((service) => service.id !== id));
      } catch (error) {
        console.error("Error deleting service:", error);
        throw error;
      } finally {
        await refreshServices();
        setServicesLoading(false);
      }
    },
    [refreshServices]
  );

  // Initial data load
  const loadInitialServices = async () => {
    try {
      setServicesLoading(true);
      const servicesData = await fetchServices();
      setServices(servicesData);
    } catch (error) {
      console.error("Error loading services:", error);
    } finally {
      setServicesLoading(false);
    }
  };

  return {
    // State
    services,
    servicesLoading,

    // Operations
    refreshServices,
    addService,
    updateService: updateServiceData,
    removeService,
    loadInitialServices,
  };
};
